import { useAuth } from "@contexts/auth";
import { COUNTRY_CODES } from "@constants/countryCodes";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Button,
  Group,
  NumberInput,
  Radio,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { api } from "@utils/api";


const schema = z.object({
  name: z.string().trim().min(1),
  category: z.enum(["homecare", "teleconsultation"]),
  price: z.number().finite().min(0),
  service_duration: z.number().int().min(1),
  sessions: z.number().int().min(1),
  session_interval_days: z.number().int().min(1),
  country_prices: z.array(z.object({ country: z.string().length(2), amount: z.number().int().min(0).max(1e12) }))
    .refine(rows => new Set(rows.map(row => row.country)).size === rows.length, "Choose each country only once"),
  scope: z.enum(["doctor", "all"]),
});

export function DoctorServiceEditor({
  profileId,
  serviceId,
  onSaved,
  onCancel,
  onDirty,
  onBusy,
}: {
  profileId: string;
  serviceId: string;
  onSaved: (id: string, name: string) => void;
  onCancel: () => void;
  onDirty: (dirty: boolean) => void;
  onBusy: (busy: boolean) => void;
}) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const client = useQueryClient();
  const url = `profile/${profileId}/services/${serviceId}/editor`;
  const query = useQuery({
    queryKey: ["doctor-service-editor", profileId, serviceId],
    refetchOnWindowFocus: false,
    queryFn: async () => (await api.get(url)).data,
  });
  const form = useForm<z.infer<typeof schema>>({
    mode: "controlled",
    validate: zodResolver(schema),
    initialValues: {
      country_prices: [],
      name: "",
      category: "teleconsultation",
      price: 0,
      service_duration: 30,
      sessions: 1,
      session_interval_days: 21,
      scope: "doctor",
    },
  });
  useEffect(() => {
    if (query.data) {
      const s = query.data.service;
      const values = {
        country_prices: Object.entries(s.country_prices ?? {}).map(([country, amount]) => ({ country, amount: Number(amount) })),
        name: s.name,
        category: s.category,
        price: s.price,
        service_duration: s.service_duration ?? 30,
        sessions: s.sessions ?? 1,
        session_interval_days: s.session_interval_days ?? 21,
        scope: "doctor" as const,
      };
      form.setValues(values);
      form.resetDirty(values);
    }
  }, [query.data]);
  const mutation = useMutation({
    mutationFn: async () => (await api.patch(url, { ...form.values, country_prices: Object.fromEntries(form.values.country_prices.map(row => [row.country, row.amount])) })).data,
    onSuccess: result => {
      client.invalidateQueries({ queryKey: ["doctor-service-editor"] });
      onBusy(false);
      onDirty(false);
      onSaved(result.service_id, form.values.name);
    },
  });
  useEffect(() => {
    onDirty(form.isDirty() || mutation.isLoading);
  }, [form.values, mutation.isLoading]);
  useEffect(() => {
    onBusy(mutation.isLoading);
    return () => onBusy(false);
  }, [mutation.isLoading]);
  if (query.isLoading) return <Text>{t("loading")}</Text>;
  if (query.isError)
    return (
      <Alert color="red">
        {t(
          "doctor-editor-service-load-error",
          "Could not load this service. Save its assignment first, then try again."
        )}
        <Button variant="subtle" onClick={onCancel}>
          {t("cancel")}
        </Button>
      </Alert>
    );
  return (
    <Stack p="md" bg="var(--mantine-color-gray-0)">
      <Text size="sm">
        {t(
          "doctor-editor-service-save-note",
          "Service changes are saved separately. Existing appointment charges are not changed."
        )}
      </Text>
      {query.data?.profile_count > 1 && (
        <Alert color="yellow">
          <Text size="sm">
            {t(
              "doctor-editor-shared-service",
              "This service is assigned to {{count}} profiles.",
              { count: query.data.profile_count }
            )}
          </Text>
          <Radio.Group {...form.getInputProps("scope")} mt="sm">
            <Stack gap="xs">
              <Radio
                value="doctor"
                label={t(
                  "doctor-editor-service-copy",
                  "Create a copy for this doctor only"
                )}
              />
              <Radio
                value="all" disabled={!can("access.manage")}
                label={t(
                  "doctor-editor-service-all",
                  "Update the shared service for all assigned profiles"
                )}
              />
            </Stack>
          </Radio.Group>
        </Alert>
      )}
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <TextInput label={t("name")} {...form.getInputProps("name")} />
        <Select
          label={t("category")}
          data={["homecare", "teleconsultation"].map(value => ({
            value,
            label: t(value),
          }))}
          {...form.getInputProps("category")}
        />
        <NumberInput
          label={`${t("price")} (XOF)`}
          min={0}
          {...form.getInputProps("price")}
        />
        <NumberInput
          label={t("service-duration-minutes", "Duration (minutes)")}
          min={1}
          allowDecimal={false}
          {...form.getInputProps("service_duration")}
        />
        <NumberInput
          label={t("consultation-service-sessions")}
          min={1}
          allowDecimal={false}
          {...form.getInputProps("sessions")}
        />
        <NumberInput
          label={t("session-interval-days")}
          disabled={form.values.sessions <= 1}
          min={1}
          allowDecimal={false}
          {...form.getInputProps("session_interval_days")}
        />
      </SimpleGrid>
      <Text fw={600}>{t("country-prices", "Country prices (XOF)")}</Text>
      <Text size="sm">{t("country-prices-help", "Saved country prices apply automatically to new bookings. The default price applies when no country rate matches.")}</Text>
      {form.values.country_prices.map((row, index) => (
        <Group key={index} align="start">
          <Select searchable label={t("patient-current-country", "Patient’s current country")}
            data={COUNTRY_CODES.filter(country => country.value === row.country || !form.values.country_prices.some(item => item.country === country.value))}
            {...form.getInputProps(`country_prices.${index}.country`)} />
          <NumberInput label="XOF" min={0} allowDecimal={false} {...form.getInputProps(`country_prices.${index}.amount`)} />
          <Button variant="subtle" onClick={() => form.removeListItem("country_prices", index)}>{t("remove", "Remove")}</Button>
        </Group>
      ))}
      {form.errors.country_prices && <Text c="red">{form.errors.country_prices}</Text>}
      <Button variant="light" onClick={() => form.insertListItem("country_prices", { country: "", amount: 0 })}>{t("add-country-price", "Add country price")}</Button>
      {mutation.isError && (
        <Alert color="red">
          {typeof (mutation.error as any)?.response?.data?.detail === "string"
            ? (mutation.error as any).response.data.detail
            : t(
                "doctor-editor-save-error",
                "Could not save. Your changes are still here."
              )}
        </Alert>
      )}
      <Group justify="flex-end">
        <Button
          variant="default"
          disabled={mutation.isLoading}
          onClick={() => {
            if (
              !form.isDirty() ||
              window.confirm(
                t("doctor-editor-discard", "Discard unsaved changes?")
              )
            ) {
              onDirty(false);
              onCancel();
            }
          }}>
          {t("cancel")}
        </Button>
        <Button
          loading={mutation.isLoading}
          disabled={!form.isDirty()}
          onClick={() => {
            if (!form.validate().hasErrors) mutation.mutate();
          }}>
          {t("doctor-editor-save-service", "Save service")}
        </Button>
      </Group>
    </Stack>
  );
}
